import React from "react";
import { useTranslation } from "react-i18next";
import { getActivityStepText } from "../ActivityStepInline/activityStepText";
import {
  formatDuration,
  formatStepDuration,
  shouldShowDuration,
  useElapsedSeconds,
  useStepElapsedSeconds,
} from "../ActivityStepInline/elapsedTime";
import "./ActivityPanel.css";

// Tarefa 1 (specs/front_planning_tools_ajustes_finais/plan.md, FR-003): favicon
// derivado em tempo de exibição a partir do domínio real já persistido - sem precisar
// de campo novo no banco. Quando não há domínio (ex.: busca via Gemini nativo, cujo
// grounding não traz `domain` na API pública), não há o que montar - cai no chip sem
// ícone, igual comportamento de hoje.
function faviconUrlFor(domain) {
  if (!domain) {
    return null;
  }
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
}

const STEP_ICON_BY_TYPE = {
  searching: "/imagens/planning-glob.svg",
  skill: "/imagens/planning-skill.svg",
  tool: "/imagens/planning-skill.svg",
  analyzing: "/imagens/planning-graph.svg",
  writing: "/imagens/planning-doc.svg",
  preparing_file: "/imagens/planning-doc.svg",
  done: "/imagens/planning-ok.svg",
};

// Sub-etapas de uma skill (tool calls) viram etapas normais na mesma timeline -
// mesmo ícone+linha conectora das demais, em vez de uma lista à parte sem
// conexão visual (ver flattenSteps).
function toolToStep(tool) {
  return {
    type: "tool",
    status: tool.status,
    startedAt: tool.startedAt,
    durationMs: tool.durationMs,
    data: { tool_label: tool.tool_label, tool_id: tool.tool_id },
  };
}

function flattenSteps(steps) {
  return steps.flatMap((step) => {
    if (
      step.type === "skill" &&
      Array.isArray(step.data?.tools) &&
      step.data.tools.length > 0
    ) {
      return [step, ...step.data.tools.map(toolToStep)];
    }
    return [step];
  });
}

function StepRow({ step, tChat }) {
  const isInProgress = step.status === "in_progress";
  const isError = step.status === "error";
  const isDone = step.type === "done";
  const isTool = step.type === "tool";
  const text = isTool ? step.data.tool_label : getActivityStepText(step, tChat);
  const data = step.data || {};
  // Tarefa 9 (FR-019): tempo individual desta etapa - "done" é sintética (sem
  // startedAt próprio) e nunca fica "em andamento", então nunca mostra tempo.
  const elapsedSeconds = useStepElapsedSeconds(
    step.startedAt,
    isInProgress,
    step.durationMs,
  );
  const iconSrc = isError
    ? "/imagens/planning-erro.svg"
    : STEP_ICON_BY_TYPE[step.type] || "/imagens/planning-doc.svg";
  const iconTestId = isDone
    ? "activity-panel-done-icon"
    : isError
      ? "activity-panel-error-icon"
      : "activity-panel-step-icon";

  return (
    <li
      className={`ActivityPanel-step${isError ? " error" : ""}`}
      data-testid={isTool ? "activity-panel-skill-tool" : "activity-panel-step"}
      data-step-type={step.type}
      data-tool-status={isTool ? step.status : undefined}
      data-output-format={
        step.type === "writing" ? data.output_format || "" : undefined
      }
    >
      <span className="ActivityPanel-step-icon-col">
        <span className="ActivityPanel-step-icon">
          <img src={iconSrc} alt="" data-testid={iconTestId} />
        </span>
        <span className="ActivityPanel-step-line" />
      </span>

      <div className="ActivityPanel-step-content">
        <span className="ActivityPanel-step-text">
          {text}
          {isTool && shouldShowDuration(elapsedSeconds) && (
            <span
              className="ActivityPanel-step-duration"
              data-testid="activity-panel-tool-duration"
            >
              {" "}
              · {formatStepDuration(elapsedSeconds)}
            </span>
          )}
        </span>

        {step.type === "searching" &&
          Array.isArray(data.sites) &&
          data.sites.length > 0 && (
            <div className="ActivityPanel-sites">
              {data.sites.map((site, siteIndex) => {
                const isTruncated = site.domain.length > 23;
                const displayDomain = isTruncated
                  ? `${site.domain.slice(0, 23)}...`
                  : site.domain;
                // title da citação (se houver) é mais útil que só o domínio truncado -
                // cai pro domínio quando não há título (ex.: site sem url válida).
                const chipTitle =
                  site.title || (isTruncated ? site.domain : undefined);
                const faviconUrl = faviconUrlFor(site.domain);
                const chipContent = (
                  <>
                    {faviconUrl && (
                      <img
                        src={faviconUrl}
                        alt=""
                        className="ActivityPanel-site-favicon"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    {displayDomain}
                  </>
                );

                // site.url pode faltar se algum outro produtor de step "searching"
                // (fora da busca web atual) só mandar domain/favicon - chip fica
                // não-clicável nesse caso, igual comportamento original.
                return site.url ? (
                  <a
                    key={site.url || `${site.domain}-${siteIndex}`}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ActivityPanel-site-chip"
                    data-testid="activity-panel-site-chip"
                    title={chipTitle}
                  >
                    {chipContent}
                  </a>
                ) : (
                  <span
                    key={site.url || `${site.domain}-${siteIndex}`}
                    className="ActivityPanel-site-chip"
                    data-testid="activity-panel-site-chip"
                    title={chipTitle}
                  >
                    {chipContent}
                  </span>
                );
              })}
            </div>
          )}

        {step.type === "skill" && data.skill_name && (
          <span
            className="ActivityPanel-skill-chip"
            data-testid="activity-panel-skill-chip"
          >
            /{data.skill_name}
          </span>
        )}
      </div>
    </li>
  );
}

// `totalDurationMs` (FR-019): total congelado da execução, vindo da mensagem
// (activity_summary.duration_ms) quando o painel reabre a timeline gravada - o
// snapshot não tem startedAt pra recalcular nada.
//
// `executionStartedAt` (activity_started_at da mensagem): início da execução, o
// MESMO instante que o indicador inline usa. Numa execução reidratada o
// startedAt de cada linha vem da duração do próprio evento, então o menor deles
// é o início do evento mais antigo ainda conhecido - deduzir o total daí fazia o
// cabeçalho marcar menos que o inline da mesma mensagem.
function ActivityPanel({
  steps,
  onClose,
  totalDurationMs,
  executionStartedAt,
}) {
  const { t: tChat } = useTranslation("chat");

  // Tarefa 9 (FR-019): tempo TOTAL (não a soma dos `ms` de cada tool). Sem
  // `executionStartedAt` (fluxo ao vivo, em que cada startedAt é relógio de
  // parede), o início é o menor startedAt entre as etapas reais - ao vivo
  // enquanto qualquer uma (fora a sintética "done") ainda estiver em andamento.
  const realSteps = steps.filter(
    (s) => s.type !== "done" && s.startedAt != null,
  );
  const inicioPelosPassos =
    realSteps.length > 0
      ? Math.min(...realSteps.map((s) => s.startedAt))
      : null;
  const isExecutionInProgress = realSteps.some(
    (s) => s.status === "in_progress",
  );
  // So' vale enquanto a execução roda: terminada, quem manda é o total congelado.
  const inicioDaExecucao =
    isExecutionInProgress && executionStartedAt != null
      ? executionStartedAt
      : inicioPelosPassos;
  const totalElapsedSeconds = useElapsedSeconds(
    inicioDaExecucao,
    isExecutionInProgress,
    totalDurationMs,
  );
  const flatSteps = flattenSteps(steps);

  return (
    <aside className="ActivityPanel" data-testid="activity-panel">
      <div className="ActivityPanel-header">
        <span className="ActivityPanel-title">
          {tChat("activityPanelTitle")}
          {shouldShowDuration(totalElapsedSeconds) && (
            <span
              className="ActivityPanel-total-duration"
              data-testid="activity-panel-total-duration"
            >
              <span>·</span>
              <span className="ActivityPanel-duration-value">
                {formatDuration(totalElapsedSeconds)}
              </span>
            </span>
          )}
        </span>
        <button
          type="button"
          className="ActivityPanel-close"
          data-testid="activity-panel-close"
          onClick={onClose}
          aria-label={tChat("closeIcon")}
        >
          <img src="/imagens/x-black.svg" alt="" />
        </button>
      </div>

      {steps.length > 0 && (
        <span
          className={`ActivityPanel-step-text${isExecutionInProgress ? " shimmer" : ""}`}
          data-testid="activity-panel-contemplating"
        >
          {tChat("activityPanelContemplating")}
        </span>
      )}

      <ol className="ActivityPanel-timeline">
        {flatSteps.map((step, index) => (
          <StepRow key={`${step.type}-${index}`} step={step} tChat={tChat} />
        ))}
      </ol>
    </aside>
  );
}

export default ActivityPanel;
